import useBatchTrainers from '../../hooks/useBatchTrainers';
import SearchableSelect from '../Form/SearchableSelect';
import {
  assignmentTrainerId,
  trainerDisplayName,
} from '../../utils/batchTrainerDisplay';

/**
 * Presentational panel for viewing / linking trainers on a batch.
 * Business/API logic lives in useBatchTrainers (SOLID: SRP).
 */
const BatchTrainersPanel = ({ batchId, initialTrainers = [], onChanged, compact = false }) => {
  const {
    canManage,
    assignments,
    availableOptions,
    selectedTrainerId,
    setSelectedTrainerId,
    loading,
    error,
    busyId,
    linkTrainer,
    unlinkTrainer,
  } = useBatchTrainers(batchId, initialTrainers, onChanged);

  const handleLink = async (e) => {
    e.preventDefault();
    await linkTrainer(selectedTrainerId);
  };

  return (
    <div className={compact ? '' : 'theme-card p-6 rounded-lg min-w-0'}>
      {!compact ? (
        <>
          <h2 className="text-xl font-semibold mb-1 theme-text block w-full">Trainers</h2>
          <p className="text-sm theme-text-secondary mb-4 leading-relaxed">
            Link trainers so they can open this batch and see its enrolled students. Controlled by
            Access Management permission <strong>Assign trainers to batches</strong>.
          </p>
        </>
      ) : null}

      {error ? (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {!canManage ? (
        <p className="mb-3 text-sm theme-text-secondary">
          You can view linked trainers. To link or unlink, an admin must grant{' '}
          <strong>Assign trainers to batches</strong> in Access Management.
        </p>
      ) : null}

      {assignments.length === 0 ? (
        <p className="text-sm theme-text-secondary mb-4">No trainers linked yet.</p>
      ) : (
        <ul className="space-y-2 mb-4">
          {assignments.map((row) => {
            const trainer = row.trainer || {};
            const tid = assignmentTrainerId(row);
            return (
              <li
                key={row.id || tid}
                className="flex items-center justify-between gap-3 rounded-lg border border-[var(--staff-line,#c9d5e5)] px-3 py-2 bg-white"
              >
                <div className="min-w-0">
                  <p className="font-medium theme-text truncate">{trainerDisplayName(trainer)}</p>
                  <p className="text-xs theme-text-secondary truncate">{trainer.email}</p>
                </div>
                {canManage ? (
                  <button
                    type="button"
                    className="shrink-0 text-sm font-semibold text-red-700 hover:underline disabled:opacity-50"
                    disabled={busyId === tid}
                    onClick={() => unlinkTrainer(tid)}
                  >
                    {busyId === tid ? '…' : 'Unlink'}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {canManage ? (
        <form onSubmit={handleLink} className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <SearchableSelect
              value={selectedTrainerId}
              onChange={(e) => setSelectedTrainerId(e.target.value)}
              disabled={loading || availableOptions.length === 0}
              placeholder={
                availableOptions.length === 0
                  ? 'No trainers available to link'
                  : 'Select trainer…'
              }
              options={availableOptions}
              getOptionLabel={(t) =>
                `${trainerDisplayName(t)}${t.email ? ` (${t.email})` : ''}`
              }
              getOptionValue={(t) => t.id}
            />
          </div>
          <button
            type="submit"
            className="theme-button-primary px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            disabled={loading || !selectedTrainerId}
          >
            {loading ? 'Linking…' : 'Link trainer'}
          </button>
        </form>
      ) : null}
    </div>
  );
};

export default BatchTrainersPanel;
